// src/components/OrgChart.js — 会社組織図（自由配置）
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import api from '../lib/api';
import './OrgChart.css';
// 役職ランクは共通モジュール（緊急連絡網・社員連絡先と統一）
import { positionRank, isPresident } from '../lib/positionRank';

//const isDisplayableDept = id => Boolean(id && !id.startsWith('00'));
const isDisplayableDept = id => {
  if(!id) return false;
  const strId = String(id).trim();
  return strId !== '0000' && strId.length > 0;
};
const isDivisionLevel = id => Boolean(id && id.length >= 4 && id.endsWith('00') && !id.startsWith('00'));

const CANVAS_WIDTH = 2200;
const CANVAS_HEIGHT = 1500;

export default function OrgChart({ highlightId, clearHighlight }) {
  const [employees, setEmployees] = useState([]);
  const [deptTree, setDeptTree] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const dragRef = useRef(null);
  const innerRef = useRef(null);
  const nodeElRefs = useRef({});
  const [edgeSegments, setEdgeSegments] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [empRes, deptRes, posRes] = await Promise.all([
          api.get('/employees'),
          api.get('/departments/tree/'),
          api.get('/organization-tree/positions/?t=${Date.now()}'),
        ]);
        setEmployees((empRes.data || []).filter(e => e.status !== 'RESIGNED'));
        setDeptTree(deptRes.data || []);
        setPositions(posRes.data?.positions || {});
      } catch (err) {
        console.error(err);
        setError('データの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const orgTree = useMemo(() => {
    // 部署内で最上位（rank最大）の社員を返す。役職名が独自呼称（マネージャー等）でも動作する。
    const findTopMember = deptName => {
      let best = null;
      let bestRank = 0; // rank 0（役職なし）だけの部署はリーダー非表示
      for (const e of employees) {
        if (e.departmentName !== deptName) continue;
        const r = positionRank(e.position);
        if (r > bestRank) { best = e; bestRank = r; }
      }
      return best;
    };
    // 社長は無所属（所属部署が空）のことが多いため、全社から役職で特定して最上位ノードに表示する。
    const president = employees.find(e => isPresident(e.position)) || null;

    const buildDeptNode = dept => {
      const name = dept.departmentName || '';
      const visibleChildren = (dept.children || []).filter(c => isDisplayableDept(c.departmentId));
      const leader = findTopMember(name);
      const forceDept = isDivisionLevel(dept.departmentId);
      const currentId = dept.departmentId ? String(dept.departmentId).trim() : String(dept.id).trim();
      const isExecutive = currentId.startsWith('00');
      return {
        //id: dept.departmentId || String(dept.id),
        //label: name,
        id: currentId,
        departmentId: currentId,
        departmentName: name,
        label: (visibleChildren.length > 0 || forceDept) ? '部署' : '課',
        personName: leader ? `${leader.position ?? ''}: ${leader.name}` : null,
        //type: (visibleChildren.length > 0 || forceDept) ? 'dept' : 'section',
        type: isExecutive ? 'president' : ((visibleChildren.length > 0 || forceDept) ? 'dept' : 'section'),
        children: visibleChildren.map(buildDeptNode),
      };
    };
    /*
    const presidents = employees.filter(e => isPresident(e.position));
    const directors = employees.filter(e => isDirector(e.position) && !isPresident(e.position));
    const senmu = employees.filter(e => isSenmu(e.position) && !isDirector(e.position) && !isPresident(e.position));
    const jomu = employees.filter(e => isJomu(e.position) && !isSenmu(e.position) && !isDirector(e.position) && !isPresident(e.position));
    //const deptNodes = deptTree.filter(d => isDisplayableDept(d.departmentId)).map(buildDeptNode);
    const deptNodes = deptTree
      .filter(d => isDisplayableDept(d.departmentId) && !d.parent_id)
      .map(buildDeptNode);
    const makeExecNode = (emp, type) => ({
      id: `exec-${type}-${emp.memberId}`,
      label: emp.position || type,
      personName: emp.name,
      type,
      children: [],
    });
    const directorNodes = [
      ...directors.map(e => makeExecNode(e, 'director')),
      ...senmu.map(e => makeExecNode(e, 'senmu')),
      ...jomu.map(e => makeExecNode(e, 'jomu')),
    ];
    const presidentNodes = presidents.map((emp, i) => ({
      id: `pres-${i}`,
      label: emp.position || '代表取締役社長' || '社長',
      personName: emp.name,
      type: 'president',
      children: i === 0 ? deptNodes : [],
    }));
    const boardChildren = presidentNodes.length
      ? [...presidentNodes, ...directorNodes]
      : (directorNodes.length ? [...directorNodes, ...deptNodes] : deptNodes);
    */
    const dbBoardNode = deptTree.find(d => String(d.departmentId).trim() === '0000' || d.departmentName === '取締役会');

    let executiveDeptsRaw = [];

    if (dbBoardNode && dbBoardNode.children && dbBoardNode.children.length > 0) {
      executiveDeptsRaw = dbBoardNode.children.filter(c => c.departmentId && String(c.departmentId).startsWith('00'));
    } else {

      executiveDeptsRaw = deptTree.filter(
        d => d.departmentId && 
          String(d.departmentId).startsWith('00') &&
          String(d.departmentId).trim() !== '0000' &&
          d.departmentName !== 'BOARD_ROOT' 
      );
    }

    const executiveNodes = executiveDeptsRaw.map(buildDeptNode);

    const presidentNode = executiveNodes.reduce((minNode, currNode) => {
      if (!minNode) return currNode;
      return parseInt(currNode.departmentId, 10) < parseInt(minNode.departmentId, 10) ? currNode : minNode;
    }, null);

   
    const normalTopDeptsRaw = deptTree.filter(
      d => isDisplayableDept(d.departmentId) && 
          (!d.parent_id || d.parent_id === "") && 
          !(d.departmentId && String(d.departmentId).startsWith('00')) &&
          d.departmentName !== '取締役会'
    );
    const normalDeptNodes = normalTopDeptsRaw.map(buildDeptNode);

    if (dbBoardNode) {
      if (presidentNode) {
        
        presidentNode.children = [...(presidentNode.children || []), ...normalDeptNodes];
      }

      const boardChildren = executiveNodes.length > 0 ? executiveNodes : normalDeptNodes;
      const boardName = dbBoardNode.departmentName || '代表取締役社長';
      // 最上位ノード名が「代表取締役社長」等の役職名なら社長本人を表示。
      // 抽象的な「取締役会」の場合はその名の社員はいないため通常は非表示（配下の役員ボックスに各自表示）。
      const boardLeader = isPresident(boardName)
        ? (president || findTopMember(boardName))
        : findTopMember(boardName);
      return [{
        id: 'board',
        label: boardName,
        departmentId: 'BOARD_ROOT',
        departmentName: boardName,
        personName: boardLeader ? `${boardLeader.position ?? ''}: ${boardLeader.name}` : null,
        type: 'board',
        children: boardChildren,
      }];
    } else {
      if (presidentNode) {
        // 社長ノードに氏名が無ければ、全社から特定した社長を表示。
        if (!presidentNode.personName && president) {
          presidentNode.personName = `${president.position ?? ''}: ${president.name}`;
        }
        presidentNode.children = [...(presidentNode.children || []), ...normalDeptNodes];
        return [presidentNode];
      }
      return normalDeptNodes;
    }
  }, [employees, deptTree]);

  const flatNodes = useMemo(() => {
    const out = [];
    const safeOrgTree = Array.isArray(orgTree) ? orgTree : [];
    const walk = (node, depth = 0, order = 0, parentId = null) => {
      out.push({ ...node, depth, order, parentId });
      (node.children || []).forEach((c, i) => walk(c, depth + 1, i, node.id));
    };
    safeOrgTree.forEach((root, i) => walk(root, 0, i, null));
    return out.map((n, i) => ({
      ...n,
      defaultX: 40 + Math.min(6, n.depth) * 300,
      defaultY: 40 + i * 88,
    }));
  }, [orgTree]);

  useLayoutEffect(() => {
    if (loading || !flatNodes.length) {
      setEdgeSegments([]);
      return;
    }
    const inner = innerRef.current;
    if (!inner) return;
    const segs = [];
    flatNodes.forEach(node => {
      if (!node.parentId) return;
      const pel = nodeElRefs.current[node.parentId];
      const cel = nodeElRefs.current[node.id];
      if (!pel || !cel) return;
      const x1 = pel.offsetLeft + pel.offsetWidth / 2;
      const y1 = pel.offsetTop + pel.offsetHeight;
      const x2 = cel.offsetLeft + cel.offsetWidth / 2;
      const y2 = cel.offsetTop;
      segs.push({ key: `${node.parentId}-${node.id}`, x1, y1, x2, y2 });
    });
    setEdgeSegments(segs);
  }, [flatNodes, positions, loading]);

  const onDragStart = (e, nodeId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const parentRect = e.currentTarget.parentElement.getBoundingClientRect();
    dragRef.current = {
      nodeId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      parentRect,
    };
  };

  const onCanvasMouseMove = e => {
    const drag = dragRef.current;
    if (!drag) return;
    const x = Math.max(0, Math.min(CANVAS_WIDTH - 220, e.clientX - drag.parentRect.left - drag.offsetX));
    const y = Math.max(0, Math.min(CANVAS_HEIGHT - 80, e.clientY - drag.parentRect.top - drag.offsetY));
    setPositions(prev => ({ ...prev, [drag.nodeId]: { x, y } }));
  };

  const onCanvasMouseUp = () => {
    dragRef.current = null;
  };

  const handleSavePositions = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.post('/organization-tree/positions/save/', { positions });
      setMessage('組織図の位置を保存しました。');

      if (typeof clearHighlight === 'function') {
        clearHighlight();
      }
    } catch (err) {
      console.error(err);
      setMessage('保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPositions = () => {
    if (!window.confirm('配置を初期位置に戻しますか？')) return;
    setPositions({});
  };

  if (loading) return <div className="oc-loading">読み込み中…</div>;
  if (error) return <div className="oc-error">{error}</div>;

  return (
    <div className="oc-free-wrap">
      <div className="oc-toolbar">
        <span className="oc-toolbar-note">ノードをドラッグして、好きな位置に配置できます。</span>
        <div className="oc-toolbar-actions">
          <button className="oc-btn" type="button" onClick={handleResetPositions}>初期化</button>
          <button className="oc-btn oc-btn-primary" type="button" onClick={handleSavePositions} disabled={saving}>
            {saving ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
      {message && <div className="oc-message">{message}</div>}
      <div
        className="oc-canvas"
        onMouseMove={onCanvasMouseMove}
        onMouseUp={onCanvasMouseUp}
        onMouseLeave={onCanvasMouseUp}
      >
        <div
          ref={innerRef}
          className="oc-canvas-inner"
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
        >
          <svg
            className="oc-edges-svg"
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            aria-hidden
          >
            {edgeSegments.map(seg => (
              <line
                key={seg.key}
                x1={seg.x1}
                y1={seg.y1}
                x2={seg.x2}
                y2={seg.y2}
                stroke="#64748b"
                strokeWidth={2}
                strokeLinecap="round"
              />
            ))}
          </svg>
          {flatNodes.map(node => {
            const p = positions[node.id] || { x: node.defaultX, y: node.defaultY };
            //新しい部署・課を追加するかどうか確認
            const isHighlighted = highlightId && node.departmentId &&
             String(node.departmentId).trim() === String(highlightId).trim();

            const isBoard = node.id === 'board' || (node.parentId === null && node.id.startsWith('00'));

            return (
              <div
                key={node.id}
                ref={el => {
                  if (el) nodeElRefs.current[node.id] = el;
                  else delete nodeElRefs.current[node.id];
                }}
                className={`oc-free-node oc-box oc-box-${node.type}`}
                style={{
                  left: `${p.x}px`,
                  top: `${p.y}px`,
                  //新しい部署・課場合色を付け
                  backgroundColor: isBoard ? '#1e293b' : (isHighlighted ? '#fef08a' : undefined),
                  border: isBoard ? '2px solid #0f172a' : (isHighlighted ? '2px solid #eab308' : undefined),
                  boxShadow: isHighlighted ? '0 0 12px rgba(234, 179, 8, 0.6)' : undefined,
                  color: isBoard ? '#ffffff' : undefined,
                }}
                onMouseDown={e => onDragStart(e, node.id)}
                title="ドラッグで移動"
              >
                <div className="oc-box-name" style={{ fontWeight: (isHighlighted || isBoard) ? 'bold' : undefined }}>
                  {node.departmentName} {isHighlighted && "🌟"}
                </div>
               
                {/*<div className="oc-box-role">{node.label}</div>*/}
                { node.personName && <div className="oc-box-person">{node.personName}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
